import Icon from '@/components/icon';
import Text from '@/components/text';
import { Box, Card, ClickAwayListener, Grid, Stack, Theme } from '@mui/material';
import { FC, MouseEvent, ReactNode, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { alpha } from '@mui/material/styles';
import TileToolbar from './tile-toolbar';
import useUserSettings from '@/hooks/use-user-settings.hook';
import classNames from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BaseTileProps } from './helpers';
import useAppTheme from '@/hooks/use-app-theme.hooks';

interface Props extends BaseTileProps {
  title: ReactNode;
  icon: ReactNode;
  onClick: () => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  tile: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: '8px',
    cursor: 'pointer',
    position: 'relative',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.background.linkbar : theme.palette.common.white,
    '& .title': {
      textAlign: 'center',
    },
    '& .tile-icon': {
      marginTop: 18,
    },
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.linkbar, 0.1)
          : theme.palette.common.white,
      '& $infoIcon': {
        display: 'block',
      },
      '& $favoriteIcon:not(.added):not(.favorite)': {
        '& .unfavorite-star': {
          display: 'block',
        },
        '&:hover': {
          '& .unfavorite-star': {
            display: 'none',
          },
          '& .favorite-star': {
            display: 'block',
            opacity: 1,
          },
        },
      },
    },
  },
  infoIcon: {
    display: 'none',
    color: theme.palette.grey[400],
    opacity: 0.6,
  },
  iconToolbar: {
    position: 'absolute',
    top: 8,
    zIndex: 10,
  },
  favoriteIcon: {
    color: theme.palette.grey[400],
    '& .favorite-star, & .unfavorite-star, & .added-star': {
      display: 'none',
      opacity: 0.6,
    },
    '&.favorite:not(.added)': {
      '& .favorite-star': {
        display: 'block',
      },
      '&:hover': {
        '& .favorite-star': {
          display: 'none',
        },
        '& .unfavorite-star': {
          display: 'block',
          opacity: 1,
        },
      },
    },
    '&.added': {
      '& .added-star': {
        display: 'block',
        opacity: 1,
      },
    },
  },
  chatChip: {
    color: theme.palette.grey[400],
    position: 'absolute',
    top: 4,
    right: 4,
    opacity: 0.6,
  },
  sortOverlay: {
    position: 'absolute',
    border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    opacity: 0.1,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    cursor: 'grab',
    zIndex: 10,
    '& .drag-handle': {
      position: 'absolute',
      top: 8,
      right: 8,
      zIndex: 10,
      color:
        theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.common.white,
    },
  },
}));

const Tile: FC<Props> = ({
  title,
  icon: IconEl,
  tile,
  onClick,
  sortable = false,
  showAccessIcon = false,
  showInfo = true,
  dotdot = false,
}) => {
  const { isDarkMode } = useAppTheme();
  const styles = useStyles();
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [showAddedStar, setShowAddedStar] = useState(false);
  const { toggleFavorite } = useUserSettings();
  const { tileId, tileType, resourceId, favorite } = tile;
  const isConversation = tileType === 'conversation' && !!resourceId;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tileId,
    disabled: !sortable,
  });

  const handleClick = () => {
    if (sortable) {
      return;
    }

    if (toolbarOpen) {
      setToolbarOpen(false);
      return;
    }

    onClick();
  };

  const handleToggleFavorite = (event: MouseEvent) => {
    event.stopPropagation();
    toggleFavorite(tile, !favorite);
    setToolbarOpen(false);

    if (!favorite) {
      setShowAddedStar(true);
      setTimeout(() => {
        setShowAddedStar(false);
      }, 2000);
    }
  };

  const handleToggleToolbar = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setToolbarOpen(!toolbarOpen);
  };

  const handleToolbarChange = () => {
    setToolbarOpen(false);
  };

  const sortCSS = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 1 : 0,
      opacity: isDragging ? 0.5 : 1,
      touchAction: 'none', // Prevents scrolling while dragging on mobile
      // cursor: 'grab',
      width: 182,
      height: 152,
    }),
    [transform, isDragging, transition]
  );

  const favoriteClass = classNames(styles.favoriteIcon, { favorite, added: showAddedStar });

  return (
    <ClickAwayListener onClickAway={handleToolbarChange} mouseEvent="onMouseDown">
      <Grid item xs={6} xxs={12} sm={4} md={4} lg={3} ref={setNodeRef} sx={sortCSS} height={150}>
        <Card
          elevation={isDarkMode ? 1 : 0}
          className={styles.tile}
          role="button"
          onClick={handleClick}
        >
          {!sortable && (
            <Stack direction="row" gap={0.5} className={styles.iconToolbar}>
              <Box className={favoriteClass} role="button" onClick={handleToggleFavorite}>
                <Icon name="favorite" size="small" className="added-star" pop />
                <Icon name="favorite" size="small" className="favorite-star" />
                <Icon name="unfavorite" size="small" className="unfavorite-star" />
              </Box>

              <Icon
                name="settings"
                size="small"
                className={styles.infoIcon}
                onClick={handleToggleToolbar}
              />
            </Stack>
          )}
          <Stack justifyContent="flex-start" alignItems="center" width="100%" height="100%">
            <Stack height="50px" alignItems="center" justifyContent="center" className="tile-icon">
              {IconEl}
            </Stack>
            <Stack justifyContent="center" alignItems="flex-start" maxWidth="96%" mt={2}>
              <Text size="small" className="title" dotdot={dotdot}>
                {title}
              </Text>
            </Stack>
          </Stack>
          <TileToolbar
            tile={tile}
            open={toolbarOpen}
            onChange={handleToolbarChange}
            showInfo={showInfo}
          />
          {(isConversation || showAccessIcon) && (
            <Stack className={styles.chatChip} direction="row" alignItems="center" gap={0.5}>
              {showAccessIcon && <Icon name="check-circle" size="x-small" />}
            </Stack>
          )}
          {sortable && (
            <Box className={styles.sortOverlay} {...attributes} {...listeners}>
              <Icon name="drag-handle" size="small" className="drag-handle" />
            </Box>
          )}
        </Card>
      </Grid>
    </ClickAwayListener>
  );
};

export default Tile;
